import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  GamePassAdminTheme,
  GamePassAdminThemeResponse,
  GamePassThemePatchRequest,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { GAME_PASS_THEME_QUERY_KEY } from "./useGetGamePassTheme";

/**
 * Merge-patches the Game Pass theme (`PATCH /gamepass/v2/admin/theme`).
 *
 * The caller sends only the dirty keys (see `buildThemeColorsPatch`); the
 * response body is the *merged* document, so we unwrap the `{ success, data }`
 * envelope and hand back the whole `GamePassAdminTheme` — including
 * `published`, which tells the caller whether the public CDN copy refreshed.
 */
export const patchGamePassTheme = async (
  body: GamePassThemePatchRequest,
): Promise<GamePassAdminTheme> => {
  const response = await getPrivateInstance().patch<GamePassAdminThemeResponse>(
    API_ROUTES.PATCH_GAME_PASS_THEME,
    body,
  );

  return response.data?.data ?? {};
};

export const usePatchGamePassTheme = (): UseMutationResult<
  GamePassAdminTheme,
  Error,
  GamePassThemePatchRequest
> => {
  const queryClient = useQueryClient();

  return useMutation<GamePassAdminTheme, Error, GamePassThemePatchRequest>({
    mutationKey: ["game-pass", "theme", "patch"],
    mutationFn: patchGamePassTheme,
    // The PATCH response is authoritative, so prime the theme query cache with
    // the merged document rather than triggering an extra refetch.
    onSuccess: (theme) => {
      queryClient.setQueryData([...GAME_PASS_THEME_QUERY_KEY], theme);
    },
  });
};
